import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const checkAuth = useCallback(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from the Google OAuth callback, skip the
    // /me check here -- AuthCallback exchanges session_id and sets the
    // session cookie first, otherwise this races and 401s.
    if (window.location.hash?.includes("session_id=")) { setAuthChecked(true); return; }
    checkAuth();
  }, [checkAuth]);

  const loginEmail = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  }, []);

  const signupEmail = useCallback(async (email, password, name) => {
    const res = await axios.post(`${API}/auth/signup`, { email, password, name }, { withCredentials: true });
    setUser(res.data);
    return res.data;
  }, []);

  const loginGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const exchangeGoogleSession = useCallback(async (sessionId) => {
    const res = await axios.post(`${API}/auth/google/session`, {}, {
      headers: { "X-Session-ID": sessionId }, withCredentials: true,
    });
    setUser(res.data);
    setAuthChecked(true);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user, authChecked, loginEmail, signupEmail, loginGoogle, exchangeGoogleSession, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// One-time Google OAuth landing screen -- reads `#session_id=` from the
// URL fragment, exchanges it for a session cookie, then hard-navigates
// home so the fragment is cleared and <App/> mounts fresh and logged in.
export function AuthCallback() {
  const { exchangeGoogleSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash || "";
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    if (!sessionId) { window.location.replace(window.location.origin + "/"); return; }
    exchangeGoogleSession(sessionId).finally(() => {
      window.location.replace(window.location.origin + "/");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null; // processed silently, no loading UI
}
